import { useCallback, useEffect, useRef } from "react";

/**
 * Returns a stable callback that defers `fn` execution by `delay` ms.
 * Pending invocations are cancelled on unmount and when `delay` changes.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delay: number,
): ((...args: TArgs) => void) & { cancel: () => void } {
  const fnRef = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel, delay]);

  const debounced = useCallback(
    (...args: TArgs) => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fnRef.current(...args);
      }, delay);
    },
    [delay],
  ) as ((...args: TArgs) => void) & { cancel: () => void };

  debounced.cancel = cancel;
  return debounced;
}
